import React, { useContext, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { Calendar } from 'primereact/calendar';
import { addLocale } from 'primereact/api';
import { postRequest } from '../../../api/ApiController';
import { LocaleContext } from '../../LocaleContext';
import UtilContext from '../../UtilContext';
import { runCrmPanelMutation, showCrmWorkflowToast } from '../../../utils/crmCommunicationPanelSave';
import { buildPrimeReactCalendarLocale, CRM_CALENDAR_INPUT_STYLE } from '../../../utils/primeReactCalendarLocale';

function formatDateToInput(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Vykonanie/Deadline – parita ServiceTaskDetail (PrimeReact Calendar, disabled, onChange + overlay).
 */
export default function CrmCommunicationExecutionDeadlinePanel({
  ticketId,
  execution,
  onChanged,
  disabled = false,
}) {
  const { t, locale } = useContext(LocaleContext);
  const { toast } = useContext(UtilContext);
  const calendarLocale = locale === 'gb' ? 'en' : locale;

  useEffect(() => {
    addLocale(calendarLocale, buildPrimeReactCalendarLocale(calendarLocale, t));
  }, [calendarLocale, t]);

  const resolvedDeadlineIso = useMemo(
    () => execution?.deadline_at || execution?.effective_deadline_at || null,
    [execution?.deadline_at, execution?.effective_deadline_at],
  );

  const calendarValue = useMemo(() => {
    if (!resolvedDeadlineIso) return null;
    const dt = DateTime.fromISO(String(resolvedDeadlineIso));
    return dt.isValid ? dt.toJSDate() : null;
  }, [resolvedDeadlineIso]);

  const currentDateKey = useMemo(() => {
    if (!resolvedDeadlineIso) return '';
    const dt = DateTime.fromISO(String(resolvedDeadlineIso));
    return dt.isValid ? dt.toFormat('yyyy-MM-dd') : '';
  }, [resolvedDeadlineIso]);

  const fieldDisabled = disabled;

  const persistDeadline = async (dateKey) => {
    if (fieldDisabled) return;
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const deadlineAt = dateKey
          ? DateTime.fromISO(dateKey).endOf('day').toUTC().toISO()
          : null;
        const res = await postRequest(
          `/api/crm_communication_tickets/${ticketId}/set_executor_step`,
          { deadline_at: deadlineAt },
          true,
        );
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.StateSaved');
          return res;
        } else {
          throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
        }
      });
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('CrmCommunication.Toast.Error'),
        detail: String(e?.message || e),
        life: 4000,
      });
    }
  };

  return (
    <div className="flex flex-col mb-5">
      <label className="font-bold mb-1">{t('ServiceTaskTable.Col.Deadline')}</label>
      <Calendar
        id={`crm-comm-deadline-${ticketId}`}
        name="dates"
        disabled={fieldDisabled}
        value={calendarValue}
        onChange={(e) => {
          if (fieldDisabled) return;
          const nextKey = e.value ? formatDateToInput(e.value) : '';
          if (nextKey === currentDateKey) return;
          void persistDeadline(nextKey || null);
        }}
        showIcon
        className="w-60"
        dateFormat="dd.mm.yy"
        firstDayOfWeek={1}
        locale={calendarLocale}
        style={CRM_CALENDAR_INPUT_STYLE}
      />
    </div>
  );
}
